import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useMsal } from "@azure/msal-react";
import { LogOut } from "lucide-react";
import { notifyAuthContextChanged } from "../utils/authCache";
import { useAccess } from "../context/AccessContext";
import { profileDisplayRole, profileShowsDepartment } from "../utils/profileDisplayRole";
import { usePerformanceTracker } from "./PerformanceTracker";
import { clearPresenceOnLogout } from "./PerformanceTracker/performanceTrackerApi";
import "./UserMenu.css";

function ProfileModal({ account, roleLabel, departmentName, onClose }) {
  if (!account) return null;

  return (
    <div className="user-menu__backdrop" onClick={onClose}>
      <div
        className="user-menu__modal"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="user-menu__modal-header">
          <h3 className="user-menu__modal-title">User Profile</h3>
          <button
            type="button"
            className="user-menu__close"
            onClick={onClose}
            aria-label="Close profile modal"
          >
            ✕
          </button>
        </div>

        <div className="user-menu__modal-body">
          <ProfileRow
            label="Full Name"
            value={account.name || account.username}
          />
          <ProfileRow label="Email Address" value={account.username} />
          {roleLabel ? <ProfileRow label="Role" value={roleLabel} /> : null}
          {departmentName ? (
            <ProfileRow label="Department" value={departmentName} />
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ProfileRow({ label, value }) {
  return (
    <div className="user-menu__row">
      <span className="user-menu__row-label">{label}</span>
      <span className="user-menu__row-value">{value}</span>
    </div>
  );
}

function UserTooltip({ anchorRef, displayName, email }) {
  const [position, setPosition] = useState(null);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;

    const rect = anchor.getBoundingClientRect();
    const viewportPadding = 16;
    const maxWidth = Math.min(
      280,
      rect.right - viewportPadding,
      window.innerWidth - viewportPadding * 2,
    );
    const right = Math.max(viewportPadding, window.innerWidth - rect.right);

    setPosition({
      top: rect.bottom + 10,
      right,
      maxWidth: Math.max(maxWidth, 160),
      arrowRight: rect.width / 2,
    });
  }, [anchorRef]);

  useEffect(() => {
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [updatePosition, displayName, email]);

  if (!position) return null;

  return createPortal(
    <div
      className="user-menu__tooltip user-menu__tooltip--fixed"
      role="tooltip"
      style={{
        top: position.top,
        right: position.right,
        maxWidth: position.maxWidth,
        "--tooltip-arrow-right": `${position.arrowRight}px`,
      }}
    >
      <div className="user-menu__tooltip-name">{displayName}</div>
      {email ? (
        <div className="user-menu__tooltip-email">{email}</div>
      ) : null}
    </div>,
    document.body,
  );
}

export default function UserMenu({ placement = "topbar", onProfileOpen }) {
  const { accounts, instance } = useMsal();
  const { isAdmin, hierarchyRoleCode, departmentName } = useAccess();
  const displayRole = profileDisplayRole({ isAdmin, hierarchyRoleCode });
  const visibleDepartment = profileShowsDepartment(displayRole)
    ? departmentName
    : null;
  const [showProfile, setShowProfile] = useState(false);
  const [shouldShowTooltip, setShouldShowTooltip] = useState(false);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const nameRef = useRef(null);
  const cardRef = useRef(null);
  const touchHideTimer = useRef(null);

  const openProfile = () => {
    onProfileOpen?.();
    setShowProfile(true);
  };

  const closeProfile = () => setShowProfile(false);

  const currentUser = accounts?.[0];
  const displayName =
    currentUser?.name || currentUser?.username || "User";
  const email = currentUser?.username || "";

  const initials = useMemo(() => {
    if (!displayName) return "U";

    return displayName
      .split(" ")
      .map((part) => part[0]?.toUpperCase())
      .slice(0, 2)
      .join("");
  }, [displayName]);

  const { stopForLogout } = usePerformanceTracker();

  const handleLogout = async () => {
    try {
      await clearPresenceOnLogout(instance, accounts);
    } catch {
      /* logout still proceeds */
    }
    try {
      await stopForLogout();
    } catch {
      /* logout still proceeds */
    }
    notifyAuthContextChanged();
    try {
      await instance.logoutRedirect({
        account: currentUser,
      });
    } catch {
      try {
        await instance.logoutPopup({
          account: currentUser,
        });
        window.location.href = "/";
      } catch (error) {
        console.error("Logout failed:", error);
      }
    }
  };

  useEffect(() => {
    const checkTooltipNeed = () => {
      const width = window.innerWidth;
      const isTabletCompact = width <= 980 && width >= 769;
      const isSmallScreen = width < 769;

      const nameEl = nameRef.current;
      const isNameTruncated =
        Boolean(nameEl) && nameEl.scrollWidth > nameEl.clientWidth;

      setShouldShowTooltip((isTabletCompact && isNameTruncated) || isSmallScreen);
    };

    checkTooltipNeed();
    window.addEventListener("resize", checkTooltipNeed);
    return () => window.removeEventListener("resize", checkTooltipNeed);
  }, [displayName, email]);

  useEffect(() => {
    return () => {
      if (touchHideTimer.current) {
        clearTimeout(touchHideTimer.current);
      }
    };
  }, []);

  const showTooltip = () => {
    if (!shouldShowTooltip) return;
    setIsTooltipVisible(true);
  };

  const hideTooltip = () => {
    if (!shouldShowTooltip) return;
    setIsTooltipVisible(false);
  };

  const handleTouchStart = () => {
    if (!shouldShowTooltip) return;
    if (touchHideTimer.current) {
      clearTimeout(touchHideTimer.current);
    }
    setIsTooltipVisible(true);
  };

  const handleTouchEnd = () => {
    if (!shouldShowTooltip) return;
    touchHideTimer.current = setTimeout(() => {
      setIsTooltipVisible(false);
    }, 1200);
  };

  return (
    <>
      <div className={`user-menu user-menu--${placement}`}>
        <div className="user-menu__card-wrapper">
          <div
            ref={cardRef}
            className="user-menu__card"
            onClick={openProfile}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                openProfile();
              }
            }}
            onMouseEnter={showTooltip}
            onMouseLeave={hideTooltip}
            onFocus={showTooltip}
            onBlur={hideTooltip}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            role="button"
            tabIndex={0}
          >
            <div className="user-menu__avatar">{initials}</div>
            <div className="user-menu__info">
              <div className="user-menu__name" ref={nameRef}>
                {displayName}
              </div>
              {(displayRole || visibleDepartment) ? (
                <div className="user-menu__meta">
                  {displayRole ? (
                    <span
                      className={`user-menu__role user-menu__role--${displayRole.tone}`}
                    >
                      {displayRole.label}
                    </span>
                  ) : null}
                  {visibleDepartment ? (
                    <span className="user-menu__dept">{visibleDepartment}</span>
                  ) : null}
                </div>
              ) : email ? (
                <div className="user-menu__email">{email}</div>
              ) : null}
            </div>
          </div>
        </div>

        {isTooltipVisible && shouldShowTooltip && (
          <UserTooltip
            anchorRef={cardRef}
            displayName={displayName}
            email={email}
          />
        )}

        <button
          type="button"
          className="user-menu__logout"
          onClick={handleLogout}
        >
          <LogOut size={16} />
          Sign Out
        </button>
      </div>

      {showProfile &&
        createPortal(
          <ProfileModal
            account={currentUser}
            roleLabel={displayRole?.label}
            departmentName={visibleDepartment}
            onClose={closeProfile}
          />,
          document.body,
        )}
    </>
  );
}
